import React, { useEffect, useState, useMemo } from "react";
import {
  FaArrowLeft,
  FaEnvelope,
  FaPhone,
  FaShareAlt,
  FaWhatsapp,
  FaTelegramPlane,
  FaFacebookF,
  FaTwitter,
  FaLinkedinIn,
  FaExternalLinkAlt,
  FaRegCopy,
} from "react-icons/fa";
import QRCode from "qrcode";
import axiosInstance from "../components/api/axiosInstance";
import AddFollowup from "./AddFollowup";
import { SupportSortableHeader } from "./SupportSortableHeader";
import TablePagination, { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "../components/TablePagination";
import "../assets/css/ViewCustomer.css";
import "../assets/css/DigixLeads.css";

const LEAD_FOLLOWUPS_ROWS_PER_PAGE_KEY = "leadFollowupsRowsPerPage";

// Legacy clipboard copy for insecure (HTTP) contexts where navigator.clipboard is unavailable
function copyTextFallback(text) {
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.top = "-9999px";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

function InfoItem({ label, value }) {
  const hasValue = value != null && String(value).trim() !== "" && value !== "—";
  const display = hasValue ? String(value).trim() : "—";
  return (
    <div className="viewlead-info-item">
      <span className={`viewlead-info-label ${hasValue ? "has-value" : ""}`}>{label}: </span>
      <span className={`viewlead-info-value ${!hasValue ? "muted" : ""}`}>{display}</span>
    </div>
  );
}

function PropertyServiceItem({ label, value, link, onShare }) {
  const hasValue = value != null && String(value).trim() !== "" && value !== "—";
  const display = hasValue ? String(value).trim() : "—";
  const url = link != null ? String(link).trim() : "";
  const isWebLink = /^https?:\/\//i.test(url);

  return (
    <div className="viewlead-info-item">
      <span className={`viewlead-info-label ${hasValue ? "has-value" : ""}`}>{label}: </span>
      <span className="viewlead-info-link-row">
        <span className={`viewlead-info-value ${!hasValue ? "muted" : ""}`}>{display}</span>
        {isWebLink && (
          <>
            <a
              className="viewlead-link-view-btn"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
            >
              View
            </a>
            {onShare && (
              <button
                type="button"
                className="viewlead-link-share-btn"
                onClick={onShare}
                title="Share link"
                aria-label="Share property / service link"
              >
                <FaShareAlt /> Share
              </button>
            )}
          </>
        )}
      </span>
    </div>
  );
}

function ViewRealestateLead({ leadId, onBack, onEditLead }) {
  const [loading, setLoading] = useState(true);
  const [lead, setLead] = useState(null);
  const [followups, setFollowups] = useState([]);
  const [activeDetailTab, setActiveDetailTab] = useState("overview");
  const [mobileQrDataUrl, setMobileQrDataUrl] = useState("");
  const [showAddFollowupModal, setShowAddFollowupModal] = useState(false);
  const [shareModal, setShareModal] = useState({ open: false, url: "", title: "" });
  const [followupSearch, setFollowupSearch] = useState("");
  const [followupDate, setFollowupDate] = useState("");
  const [followupSortColumn, setFollowupSortColumn] = useState("followupDate");
  const [followupSortDirection, setFollowupSortDirection] = useState("desc");
  const [followupPage, setFollowupPage] = useState(1);
  const [followupRowsPerPage, setFollowupRowsPerPage] = useState(() => {
    try {
      const saved = localStorage.getItem(LEAD_FOLLOWUPS_ROWS_PER_PAGE_KEY);
      const parsed = parseInt(saved, 10);
      if (DEFAULT_ROWS_PER_PAGE_OPTIONS.includes(parsed)) {
        return parsed;
      }
    } catch {
      /* ignore */
    }
    return 5;
  });
  const [editingFollowup, setEditingFollowup] = useState(null);
  const [followupEditForm, setFollowupEditForm] = useState({
    followupDate: "",
    followupType: "",
    remarks: "",
    nextFollowupDate: "",
  });
  const [updatingFollowup, setUpdatingFollowup] = useState(false);

  const fetchFollowups = async () => {
    if (!leadId) return;
    try {
      const response = await axiosInstance.get(`/real-estate-lead-followups/${leadId}`);
      if (response.data && Array.isArray(response.data)) {
        setFollowups(response.data);
      } else {
        setFollowups([]);
      }
    } catch (err) {
      console.error("Error fetching real estate followups:", err);
      setFollowups([]);
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(`/real-estate-lead/${leadId}`);
        // API returns object directly: { Real_Estate_Lead_Id, First_Name, ... }
        const data =
          response.data?.real_estate_lead ||
          response.data?.realEstateLead ||
          response.data;
        setLead(data && typeof data === "object" ? data : null);
      } catch (err) {
        console.error("Error fetching real estate lead:", err);
        setLead(null);
        alert("Failed to load real estate lead details.");
      } finally {
        setLoading(false);
      }
    };
    if (leadId) load();
  }, [leadId]);

  useEffect(() => {
    fetchFollowups();
  }, [leadId]);

  // Generate a QR code that opens the dialer with the lead's mobile number when scanned
  useEffect(() => {
    const rawMobile = lead?.Mobile_Number ? String(lead.Mobile_Number).trim() : "";
    if (!rawMobile) {
      setMobileQrDataUrl("");
      return;
    }
    const telUri = `tel:${`${lead?.Country_Code || ""}${rawMobile}`.replace(/[^\d+]/g, "")}`;
    QRCode.toDataURL(telUri, { width: 220, margin: 1 })
      .then((url) => setMobileQrDataUrl(url))
      .catch((err) => {
        console.error("Error generating mobile QR code:", err);
        setMobileQrDataUrl("");
      });
  }, [lead?.Mobile_Number, lead?.Country_Code]);

  const filteredFollowups = useMemo(() => {
    let list = [...followups];
    if (followupSearch.trim()) {
      const q = followupSearch.toLowerCase().trim();
      list = list.filter(
        (f) =>
          (f.Remarks || "").toLowerCase().includes(q) ||
          (f.Followup_Type || "").toLowerCase().includes(q)
      );
    }
    if (followupDate) {
      list = list.filter((f) => {
        const dateValues = [
          f.Followup_Date || f.followup_date,
          f.Next_Followup_Date || f.next_followup_date,
          f.Created_On || f.Created_At || f.created_on || f.created_at,
          f.Updated_On || f.Updated_At || f.updated_on || f.updated_at,
        ];

        return dateValues.some((value) => {
          if (!value) return false;
          const date = new Date(value);
          if (Number.isNaN(date.getTime())) return false;
          const localDate = [
            date.getFullYear(),
            String(date.getMonth() + 1).padStart(2, "0"),
            String(date.getDate()).padStart(2, "0"),
          ].join("-");
          return localDate === followupDate;
        });
      });
    }
    return list;
  }, [followups, followupSearch, followupDate]);

  const getFollowupSortValue = (f, columnId) => {
    if (columnId === "followupDate") {
      const t = new Date(f.Followup_Date || f.followup_date || "").getTime();
      return Number.isFinite(t) ? t : 0;
    }
    if (columnId === "type") return String(f.Followup_Type || f.followup_type || "").toLowerCase();
    if (columnId === "remarks") return String(f.Remarks || f.remarks || "").toLowerCase();
    if (columnId === "nextFollowupDate") {
      const t = new Date(f.Next_Followup_Date || f.next_followup_date || "").getTime();
      return Number.isFinite(t) ? t : 0;
    }
    if (columnId === "createdAt") {
      const t = new Date(f.Created_On || f.Created_At || f.created_on || f.created_at || "").getTime();
      return Number.isFinite(t) ? t : 0;
    }
    return "";
  };

  const sortedFollowups = useMemo(() => {
    if (!followupSortColumn) return filteredFollowups;
    const mult = followupSortDirection === "desc" ? -1 : 1;
    return [...filteredFollowups].sort((a, b) => {
      const va = getFollowupSortValue(a, followupSortColumn);
      const vb = getFollowupSortValue(b, followupSortColumn);
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      const ida = Number(a?.Followup_Id ?? a?.followup_id ?? a?.id ?? 0);
      const idb = Number(b?.Followup_Id ?? b?.followup_id ?? b?.id ?? 0);
      return (ida - idb) * mult;
    });
  }, [filteredFollowups, followupSortColumn, followupSortDirection]);

  const followupTotalPages = Math.max(1, Math.ceil(sortedFollowups.length / followupRowsPerPage));
  const followupStartIdx = (followupPage - 1) * followupRowsPerPage;
  const currentFollowups = sortedFollowups.slice(followupStartIdx, followupStartIdx + followupRowsPerPage);

  useEffect(() => {
    if (followupPage > followupTotalPages && followupTotalPages > 0) {
      setFollowupPage(1);
    }
  }, [followupPage, followupTotalPages, sortedFollowups.length]);

  const handleFollowupRowsPerPageChange = (next) => {
    setFollowupRowsPerPage(next);
    setFollowupPage(1);
    localStorage.setItem(LEAD_FOLLOWUPS_ROWS_PER_PAGE_KEY, String(next));
  };

  const handleFollowupSort = (columnId) => {
    setFollowupPage(1);
    if (followupSortColumn === columnId) {
      setFollowupSortDirection((direction) => direction === "asc" ? "desc" : "asc");
    } else {
      setFollowupSortColumn(columnId);
      setFollowupSortDirection("asc");
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return "—";
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const hours = String(d.getHours()).padStart(2, "0");
      const minutes = String(d.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    } catch {
      return "—";
    }
  };

  const toDateTimeLocal = (dateStr) => {
    if (!dateStr) return "";
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return "";
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const hours = String(d.getHours()).padStart(2, "0");
      const minutes = String(d.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return "";
    }
  };

  const handleEditFollowup = (f) => {
    setEditingFollowup(f);
    setFollowupEditForm({
      followupDate: toDateTimeLocal(f.Followup_Date || f.followup_date),
      followupType: f.Followup_Type || f.followup_type || "",
      remarks: f.Remarks || f.remarks || "",
      nextFollowupDate: toDateTimeLocal(f.Next_Followup_Date || f.next_followup_date),
    });
  };

  const handleCloseEditFollowup = () => {
    setEditingFollowup(null);
    setFollowupEditForm({ followupDate: "", followupType: "", remarks: "", nextFollowupDate: "" });
  };

  const handleFollowupEditChange = (e) => {
    const { name, value } = e.target;
    setFollowupEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdateFollowupSubmit = async (e) => {
    e.preventDefault();
    if (!editingFollowup) return;
    const followupId = editingFollowup.Followup_Id || editingFollowup.followup_id || editingFollowup.id;
    if (!followupId) return;

    const formatForApi = (dateTimeString) => {
      if (!dateTimeString) return null;
      return (dateTimeString + ":00").replace("T", " ");
    };

    let updatedBy = 1;
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        updatedBy = user.user_id || user.id || 1;
      }
    } catch (err) {
      console.error("Error reading user for Updated_By:", err);
    }

    setUpdatingFollowup(true);
    try {
      await axiosInstance.put(`/update-real-estate-followup/${followupId}`, {
        Real_Estate_Lead_Id: leadId,
        Followup_Date: formatForApi(followupEditForm.followupDate),
        Followup_Type: followupEditForm.followupType || null,
        Remarks: followupEditForm.remarks || null,
        Next_Followup_Date: formatForApi(followupEditForm.nextFollowupDate),
        Updated_By: updatedBy,
      });
      alert("Followup updated successfully.");
      handleCloseEditFollowup();
      fetchFollowups();
    } catch (error) {
      console.error("Error updating real estate followup:", error);
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to update followup.";
      alert(msg);
    } finally {
      setUpdatingFollowup(false);
    }
  };

  const handleDeleteFollowup = async (followupId) => {
    if (!window.confirm("Are you sure you want to delete this followup?")) return;
    try {
      const response = await axiosInstance.delete(`/real-estate-followup/${followupId}`);
      if (response.data?.message) {
        alert(response.data.message);
      }
      fetchFollowups();
    } catch (error) {
      console.error("Error deleting real estate followup:", error);
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to delete followup.";
      alert(msg);
    }
  };

  // Always open our own share menu so it works identically on http and https.
  const handleSharePropertyService = () => {
    const url = String(lead?.Property_Service_Link || "").trim();
    if (!/^https?:\/\//i.test(url)) {
      alert("No property / service link is available to share.");
      return;
    }
    const title = lead.Property_Service || lead.Property_Service_Name || "Property / Service";
    setShareModal({ open: true, url, title });
  };

  const closeShareModal = () => setShareModal({ open: false, url: "", title: "" });

  // Native OS share sheet — only available in secure (https/localhost) contexts.
  const handleNativeShare = async () => {
    if (!navigator.share) return;
    try {
      await navigator.share({
        title: shareModal.title,
        text: `View ${shareModal.title}`,
        url: shareModal.url,
      });
      closeShareModal();
    } catch (error) {
      if (error?.name === "AbortError") return;
    }
  };

  const handleCopyShareLink = async () => {
    const url = shareModal.url;
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(url);
        alert("Property / service link copied to clipboard.");
        return;
      } catch {
        /* fall through to legacy copy */
      }
    }
    if (copyTextFallback(url)) {
      alert("Property / service link copied to clipboard.");
    } else {
      window.prompt("Copy this property / service link:", url);
    }
  };

  // Format mobile number with country code; alt phone stays without country code
  const formatMobileWithCountryCode = (lead) => {
    const mobile = lead?.Mobile_Number;
    if (!mobile || String(mobile).trim() === "") return "—";
    const code = (lead?.Country_Code || "+91").trim();
    return `${code} ${String(mobile).trim()}`;
  };

  if (loading) {
    return (
      <div className="viewlead-container">
        <div className="viewlead-loading">Loading lead details...</div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="viewlead-container">
        <button className="viewlead-back-link" onClick={onBack}>
          <FaArrowLeft /> Back to Real Estate Leads
        </button>
        <div className="viewlead-loading">Real estate lead not found.</div>
      </div>
    );
  }

  const fullName = `${lead.First_Name || ""} ${lead.Last_Name || ""}`.trim() || "Real Estate Lead";
  const mobile = formatMobileWithCountryCode(lead);
  const phoneHref = `${lead.Country_Code || ""}${lead.Mobile_Number || ""}`.replace(/[^\d+]/g, "");
  const budgetRange =
    lead.Budget_Min || lead.Budget_Max
      ? `${lead.Budget_Min || "—"} - ${lead.Budget_Max || "—"}`
      : "—";

  return (
    <div className="viewlead-container realestate-view-page">
      {/* Header */}
      <div className="viewlead-header">
        <button className="viewlead-back-link" onClick={onBack}>
          <FaArrowLeft /> Back to Real Estate Leads
        </button>
        <div className="viewlead-header-actions">
          {onEditLead && (
            <button className="btn-edit-lead" type="button" onClick={() => onEditLead(leadId, "realestate-lead")}>
              Edit Lead
            </button>
          )}
        </div>
      </div>

      <section className="re-crm-summary-card">
        <div className="re-crm-avatar">
          {`${lead.First_Name?.[0] || ""}${lead.Last_Name?.[0] || ""}`.toUpperCase() || "RE"}
        </div>
        <div className="re-crm-summary-main">
          <div className="re-crm-name-row">
            <h2>{fullName}</h2>
          </div>
          <div className="re-crm-contact-strip re-crm-quick-look">
            {lead.Mobile_Number && (
              <a href={`tel:${phoneHref}`}><FaPhone /> {mobile}</a>
            )}
            {lead.Email && (
              <a href={`mailto:${lead.Email}`}><FaEnvelope /> {lead.Email}</a>
            )}
            <span className="re-crm-quick-status">
              {lead.Lead_Status || "No Status"}
            </span>
          </div>
        </div>
      </section>

      <div className="re-crm-detail-layout re-crm-tabbed-layout">
        <main className="re-crm-overview">
          <div className="re-crm-tabs" role="tablist" aria-label="Lead details">
            <button
              type="button"
              role="tab"
              aria-selected={activeDetailTab === "overview"}
              className={activeDetailTab === "overview" ? "active" : ""}
              onClick={() => setActiveDetailTab("overview")}
            >
              Overview
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeDetailTab === "activity"}
              className={activeDetailTab === "activity" ? "active" : ""}
              onClick={() => setActiveDetailTab("activity")}
            >
              Followups
            </button>
          </div>

          {activeDetailTab === "overview" ? (
            <div className="re-crm-overview-body">
              <section className="re-crm-info-card re-crm-lead-info-card">
                <h3>Lead Information</h3>
                <div className="re-crm-lead-info-body">
                  <div className="re-crm-info-grid">
                    <InfoItem label="Customer" value={lead.Customer_Name} />
                    <InfoItem label="Email" value={lead.Email} />
                    <InfoItem label="Mobile" value={mobile} />
                    <InfoItem label="Alternate Phone" value={lead.Alt_Phone} />
                    <InfoItem label="Lead Location" value={lead.Preferred_Location} />
                  </div>
                  {mobileQrDataUrl && (
                    <div className="re-crm-qr">
                      <img src={mobileQrDataUrl} alt={`QR code to dial ${mobile}`} />
                      <span>Scan to dial</span>
                    </div>
                  )}
                </div>
              </section>

              <section className="re-crm-info-card">
                <h3>Property Requirements</h3>
                <div className="re-crm-info-grid">
                  <PropertyServiceItem
                    label="Property / Service"
                    value={lead.Property_Service || lead.Property_Service_Name}
                    link={lead.Property_Service_Link}
                    onShare={handleSharePropertyService}
                  />
                  <InfoItem label="Property Location" value={lead.Property_Location} />
                  <InfoItem label="Property Type" value={lead.Property_Type_Name} />
                  <InfoItem label="Requirement Type" value={lead.Requirement_Type} />
                  <InfoItem label="Budget Range" value={budgetRange} />
                  <InfoItem label="Site Visit" value={lead.Site_Visit ? "Yes" : "No"} />
                </div>
              </section>

              <section className="re-crm-info-card re-crm-additional-card">
                <h3>Additional Information</h3>
                <div className="re-crm-info-grid re-crm-info-grid-wide">
                  <InfoItem label="Status" value={lead.Lead_Status} />
                  <InfoItem label="Source" value={lead.Source_Name} />
                  <InfoItem label="Opportunity" value={lead.Is_Opportunity ? "Yes" : "No"} />
                  <InfoItem label="Automation" value={lead.Automation_Enabled ? "Yes" : "No"} />
                  <InfoItem
                    label="Created By"
                    value={lead.Created_By_Name || (lead.Created_By != null ? `User #${lead.Created_By}` : null)}
                  />
                  <InfoItem
                    label="Updated By"
                    value={lead.Updated_By_Name || (lead.Updated_By != null ? `User #${lead.Updated_By}` : null)}
                  />
                  <InfoItem label="Created On" value={formatDateTime(lead.Created_On)} />
                  <InfoItem label="Updated On" value={formatDateTime(lead.Updated_On)} />
                  <InfoItem label="Notes" value={lead.Notes} />
                </div>
              </section>
            </div>
          ) : (
            <section className="re-crm-activity re-crm-activity-tab" role="tabpanel">
              <div className="re-crm-activity-filters">
                <input
                  type="text"
                  className="viewlead-search-input"
                  placeholder="Search followups..."
                  value={followupSearch}
                  onChange={(e) => {
                    setFollowupSearch(e.target.value);
                    setFollowupPage(1);
                  }}
                />
                <input
                  type="date"
                  className="viewlead-date-input"
                  value={followupDate}
                  onChange={(e) => {
                    setFollowupDate(e.target.value);
                    setFollowupPage(1);
                  }}
                />
                <button type="button" className="btn-add-followup" onClick={() => setShowAddFollowupModal(true)}>
                  + New
                </button>
              </div>

              {sortedFollowups.length > 0 ? (
                <>
                  <div className="re-crm-followups-table-wrap">
                    <table className="viewlead-table viewlead-followups-table re-crm-followups-table">
                      <thead>
                        <tr>
                          <th className="viewlead-th-sortable">
                            <div className="viewlead-th-sort-row viewlead-th-sort-row--center">
                              <SupportSortableHeader columnId="followupDate" label="Followup Date" sortColumn={followupSortColumn} sortDirection={followupSortDirection} onSort={handleFollowupSort} />
                            </div>
                          </th>
                          <th className="viewlead-th-sortable">
                            <div className="viewlead-th-sort-row viewlead-th-sort-row--center">
                              <SupportSortableHeader columnId="type" label="Type" sortColumn={followupSortColumn} sortDirection={followupSortDirection} onSort={handleFollowupSort} />
                            </div>
                          </th>
                          <th className="viewlead-th-sortable">
                            <div className="viewlead-th-sort-row viewlead-th-sort-row--center">
                              <SupportSortableHeader columnId="remarks" label="Remarks" sortColumn={followupSortColumn} sortDirection={followupSortDirection} onSort={handleFollowupSort} />
                            </div>
                          </th>
                          <th className="viewlead-th-sortable">
                            <div className="viewlead-th-sort-row viewlead-th-sort-row--center">
                              <SupportSortableHeader columnId="nextFollowupDate" label="Next Followup Date" sortColumn={followupSortColumn} sortDirection={followupSortDirection} onSort={handleFollowupSort} />
                            </div>
                          </th>
                          <th className="viewlead-th-sortable">
                            <div className="viewlead-th-sort-row viewlead-th-sort-row--center">
                              <SupportSortableHeader columnId="createdAt" label="Created At" sortColumn={followupSortColumn} sortDirection={followupSortDirection} onSort={handleFollowupSort} />
                            </div>
                          </th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentFollowups.map((f, idx) => {
                          const followupId = f.Followup_Id || f.followup_id || f.id;
                          return (
                            <tr key={followupId || idx}>
                              <td>{formatDateTime(f.Followup_Date || f.followup_date)}</td>
                              <td>{f.Followup_Type || f.followup_type || "—"}</td>
                              <td className="re-crm-followup-remarks">{f.Remarks || f.remarks || "—"}</td>
                              <td>{formatDateTime(f.Next_Followup_Date || f.next_followup_date)}</td>
                              <td>
                                {formatDateTime(
                                  f.Created_On || f.Created_At || f.created_on || f.created_at
                                )}
                              </td>
                              <td>
                                {followupId ? (
                                  <div className="viewlead-followup-actions">
                                    <button type="button" className="btn-edit-followup" onClick={() => handleEditFollowup(f)}>
                                      Edit
                                    </button>
                                    <button type="button" className="btn-delete-followup" onClick={() => handleDeleteFollowup(followupId)}>
                                      Delete
                                    </button>
                                  </div>
                                ) : (
                                  "—"
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <TablePagination
                    currentPage={followupPage}
                    totalItems={sortedFollowups.length}
                    rowsPerPage={followupRowsPerPage}
                    rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                    onPageChange={setFollowupPage}
                    onRowsPerPageChange={handleFollowupRowsPerPageChange}
                    itemLabel="followups"
                    rowsPerPageSelectId="realestate-lead-followups-rows-per-page"
                  />
                </>
              ) : (
                <p className="viewlead-no-data">No followups found.</p>
              )}
            </section>
          )}
        </main>
      </div>

      {/* Add Followup Modal */}
      {showAddFollowupModal && (
        <div
          className="viewlead-modal-overlay viewlead-addfollowup-overlay"
          onClick={() => setShowAddFollowupModal(false)}
        >
          <div
            className="viewlead-modal-content viewlead-addfollowup-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="viewlead-modal-header">
              <h3 className="viewlead-modal-title">New Followup</h3>
              <button
                type="button"
                className="viewlead-modal-close"
                onClick={() => setShowAddFollowupModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="viewlead-addfollowup-modal-body">
              <AddFollowup
                id={leadId}
                type="realestate-lead"
                onBack={() => {
                  setShowAddFollowupModal(false);
                  fetchFollowups();
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Share Property / Service Modal (fallback when native share is unavailable) */}
      {shareModal.open && (
        <div className="viewlead-modal-overlay" onClick={closeShareModal}>
          <div
            className="viewlead-modal-content viewlead-share-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="viewlead-modal-header">
              <h3 className="viewlead-modal-title">Share link</h3>
              <button
                type="button"
                className="viewlead-modal-close"
                onClick={closeShareModal}
              >
                &times;
              </button>
            </div>
            <div className="viewlead-share-modal-body">
              <p className="viewlead-share-title" title={shareModal.title}>
                {shareModal.title}
              </p>
              <div className="viewlead-share-url" title={shareModal.url}>
                {shareModal.url}
              </div>
              <div className="viewlead-share-options">
                <a
                  className="viewlead-share-option whatsapp"
                  href={`https://wa.me/?text=${encodeURIComponent(`${shareModal.title}\n${shareModal.url}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeShareModal}
                >
                  <FaWhatsapp /> WhatsApp
                </a>
                <a
                  className="viewlead-share-option telegram"
                  href={`https://t.me/share/url?url=${encodeURIComponent(shareModal.url)}&text=${encodeURIComponent(shareModal.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeShareModal}
                >
                  <FaTelegramPlane /> Telegram
                </a>
                <a
                  className="viewlead-share-option facebook"
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareModal.url)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeShareModal}
                >
                  <FaFacebookF /> Facebook
                </a>
                <a
                  className="viewlead-share-option twitter"
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareModal.url)}&text=${encodeURIComponent(shareModal.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeShareModal}
                >
                  <FaTwitter /> Twitter
                </a>
                <a
                  className="viewlead-share-option linkedin"
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareModal.url)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeShareModal}
                >
                  <FaLinkedinIn /> LinkedIn
                </a>
                <a
                  className="viewlead-share-option email"
                  href={`mailto:?subject=${encodeURIComponent(shareModal.title)}&body=${encodeURIComponent(`View ${shareModal.title}:\n${shareModal.url}`)}`}
                  onClick={closeShareModal}
                >
                  <FaEnvelope /> Email
                </a>
                <a
                  className="viewlead-share-option open"
                  href={shareModal.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaExternalLinkAlt /> Open
                </a>
                <button
                  type="button"
                  className="viewlead-share-option copy"
                  onClick={handleCopyShareLink}
                >
                  <FaRegCopy /> Copy link
                </button>
                {typeof navigator !== "undefined" && navigator.share && (
                  <button
                    type="button"
                    className="viewlead-share-option more"
                    onClick={handleNativeShare}
                  >
                    <FaShareAlt /> More apps
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Followup Modal */}
      {editingFollowup && (
        <div
          className="viewlead-modal-overlay"
          onClick={() => !updatingFollowup && handleCloseEditFollowup()}
        >
          <div
            className="viewlead-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="viewlead-modal-header">
              <h3 className="viewlead-modal-title">Edit Followup</h3>
              <button
                type="button"
                className="viewlead-modal-close"
                onClick={handleCloseEditFollowup}
                disabled={updatingFollowup}
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleUpdateFollowupSubmit} className="viewlead-form-inner">
              <div className="viewlead-form-group">
                <label>Followup Date <span className="required">*</span></label>
                <input
                  type="datetime-local"
                  name="followupDate"
                  value={followupEditForm.followupDate}
                  onChange={handleFollowupEditChange}
                  required
                  disabled={updatingFollowup}
                />
              </div>
              <div className="viewlead-form-group">
                <label>Followup Type</label>
                <input
                  type="text"
                  name="followupType"
                  value={followupEditForm.followupType}
                  onChange={handleFollowupEditChange}
                  placeholder="e.g. Call, Email"
                  disabled={updatingFollowup}
                />
              </div>
              <div className="viewlead-form-group">
                <label>Remarks</label>
                <textarea
                  name="remarks"
                  value={followupEditForm.remarks}
                  onChange={handleFollowupEditChange}
                  rows={3}
                  disabled={updatingFollowup}
                />
              </div>
              <div className="viewlead-form-group">
                <label>Next Followup Date</label>
                <input
                  type="datetime-local"
                  name="nextFollowupDate"
                  value={followupEditForm.nextFollowupDate}
                  onChange={handleFollowupEditChange}
                  disabled={updatingFollowup}
                />
              </div>
              <div className="viewlead-modal-actions">
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={updatingFollowup}
                >
                  {updatingFollowup ? "Updating..." : "Update Followup"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ViewRealestateLead;

